import { useRef, useState } from 'react'

import { BordersStyles, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { DocumentsUploadProcessOperationItemFragment } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'

import { useManualDocumentAssignerContext } from '../../../context'
import Discarded from './Discarded'
import EmployeeAssigned from './EmployeeAssigned'
import EmployeeAssignedSkeleton from './EmployeeAssignedSkeleton'
import EmployeeAssigner from './EmployeeAssigner'

interface Props {
  fileId: number
  page: number
  operations: DocumentsUploadProcessOperationItemFragment[]
  showPageNumber?: boolean
  onClickPage?: (page: number) => void
  onEmployeeSelected: (employeeId: number, page: number) => void
  onDiscardPage: (page: number) => void
  onRemoveEmployee: (page: number) => void
  onEnablePage: (page: number) => void
  contractVersions: ContractVersions
}

const PageAssigner = ({
  fileId,
  page,
  operations,
  showPageNumber,
  onClickPage,
  onEmployeeSelected,
  onDiscardPage,
  onRemoveEmployee,
  onEnablePage,
  contractVersions,
}: Props) => {
  const context = useManualDocumentAssignerContext()
  const employeeAssigmentOperation = operations.find(
    (o) => o.operationType == 'employee_assignment'
  )
  const [loading, setLoading] = useState<boolean>(false)

  const ref = useRef<HTMLElement>(null)

  const discardOperation = operations.find((o) => o.operationType == 'discard')

  const mergeOperation = operations.find((o) => o.operationType == 'merge')

  const employeeName = employeeAssigmentOperation
    ? employeeAssigmentOperation?.employee?.fullName
    : i18n.t('split_documents.workflow_payslips.page_assigner.select_employee')

  const selectedPage = useMemo(() => {
    return context.selectedFileId == fileId && context.selectedPage == page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.selectedFileId, context.selectedPage])

  const border: BordersStyles = {
    bottom: {
      width: 's1',
      style: 'solid',
      color: 'grey400',
    },
  }

  useUnsafeEffect(() => {
    setLoading(false)
  }, [operations])

  const onClick = () => {
    if (!context.loading && onClickPage) {
      centerPage()
      onClickPage(page)
    }
  }

  const onRemovePageEmployee = useUnsafeCallback(() => {
    onRemoveEmployee(page)
    setLoading(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRemoveEmployee])

  const onSelectEmployee = useUnsafeCallback(
    (employeeId: number) => {
      onEmployeeSelected(employeeId, page)
      setLoading(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEmployeeSelected]
  )

  const onDiscard = useUnsafeCallback(() => {
    onDiscardPage(page)
    setLoading(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDiscardPage])

  const onEnable = useUnsafeCallback(() => {
    onEnablePage(page)
    setLoading(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEnablePage])

  useEffect(() => {
    if (!loading && selectedPage) {
      centerPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const centerPage = () => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  return (
    <>
      <Box
        key={page}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        padding="s12"
        gap="s8"
        cursor="pointer"
        onClick={onClick}
        style={{ width: '100%' }}
        border={border}
        ref={ref}
      >
        <Box flexDirection="row">
          {showPageNumber && (
            <Box flexDirection="row" width="s40">
              <Text size="300" weight="medium" color="grey800">
                {page + 1}
              </Text>
            </Box>
          )}
          <Box flexDirection="row">
            {!discardOperation && (
              <Text size="300" weight="medium" color="textPrimary">
                {employeeName}
              </Text>
            )}
            {discardOperation && (
              <Text size="300" weight="medium" color="grey800">
                {i18n.t('split_documents.workflow_payslips.page_assigner.discarded')}
              </Text>
            )}
          </Box>
        </Box>
        <Box flexDirection="row" gap="s4">
          {!employeeAssigmentOperation && !discardOperation && (
            <Box flexDirection="row">
              <Icon.Medium icon="alert-circle" color="radicalRed1000" />
            </Box>
          )}
          {discardOperation && (
            <Box flexDirection="row">
              <Icon.Medium icon="delete" color="grey700" />
            </Box>
          )}
          <Box flexDirection="row">
            {!selectedPage && <Icon.Medium icon="chevron-up" />}
            {selectedPage && <Icon.Medium icon="chevron-down" />}
          </Box>
        </Box>
      </Box>
      {!loading && selectedPage && employeeAssigmentOperation && (
        <EmployeeAssigned
          employeeAssigmentOperation={employeeAssigmentOperation}
          isMerged={mergeOperation !== undefined}
          onRemoveEmployee={onRemovePageEmployee}
        />
      )}
      {!loading && selectedPage && discardOperation && <Discarded onEnable={onEnable} />}
      {!loading && selectedPage && !employeeAssigmentOperation && !discardOperation && (
        <EmployeeAssigner
          onEmployeeSelected={onSelectEmployee}
          onDiscard={onDiscard}
          contractVersions={contractVersions}
        />
      )}
      {selectedPage && loading && <EmployeeAssignedSkeleton />}
    </>
  )
}

export default PageAssigner
